import { useMemo } from 'react'

import { useQuinceRouter } from '@src/modules/navigation'

import { Filter, SelectedFilter } from '../menu/filterSection.interface'
import {
  getEnhancedSelectedFilterChips,
  getEnhancedClearAllFilters,
  getEnhancedHandleChipDismissByLabel,
} from '../menu/filterUtils'

import { useEnhancedFilterCount } from './useEnhancedFilterCount'
import { useFilterState } from './useFilterState'

interface UseEnhancedFilterStateProps {
  selectedFilters: SelectedFilter
  showGroupedPage: boolean
  routerAsPath: string
  collectionSlug?: string
  serverFilters: Filter[]
  updateQuery: (params: any) => void
}

interface UseEnhancedFilterStateReturn {
  selectedFiltersState: Record<string, string[]>
  selectedFilterChips: { label: string; filterType: string; value: string }[]
  clearAllFilters: () => void
  handleCheckboxFilterChange: (filter: any, selectedOptions: any[]) => void
  handleColorChange: (filter: any, value: string | string[]) => void
  handleChipDismissByLabel: (label: string) => void
  effectiveTotalSelectedFilters: number
}

export const useEnhancedFilterState = ({
  selectedFilters,
  showGroupedPage,
  routerAsPath,
  collectionSlug,
  serverFilters,
  updateQuery,
}: UseEnhancedFilterStateProps): UseEnhancedFilterStateReturn => {
  const router = useQuinceRouter()

  // Use the base filter state hook
  const {
    selectedFiltersState,
    selectedFilterChips: baseSelectedFilterChips,
    clearAllFilters: baseClearAllFilters,
    handleCheckboxFilterChange,
    handleColorChange,
    handleChipDismissByLabel: baseHandleChipDismissByLabel,
  } = useFilterState(selectedFilters, updateQuery)

  // Enhanced chip generation using utility function
  const selectedFilterChips = useMemo(() => {
    return getEnhancedSelectedFilterChips(
      baseSelectedFilterChips,
      showGroupedPage,
      routerAsPath,
      collectionSlug,
      serverFilters,
      selectedFiltersState,
    )
  }, [baseSelectedFilterChips, showGroupedPage, routerAsPath, collectionSlug, serverFilters, selectedFiltersState])

  // Enhanced clear all function using utility function
  const clearAllFilters = useMemo(() => {
    return getEnhancedClearAllFilters(
      baseClearAllFilters,
      showGroupedPage,
      routerAsPath,
      collectionSlug,
      serverFilters,
      selectedFiltersState,
      router,
    )
  }, [baseClearAllFilters, showGroupedPage, routerAsPath, collectionSlug, serverFilters, selectedFiltersState, router])

  // Enhanced chip dismiss function using utility function
  const handleChipDismissByLabel = useMemo(() => {
    return getEnhancedHandleChipDismissByLabel(
      baseHandleChipDismissByLabel,
      showGroupedPage,
      routerAsPath,
      serverFilters,
      router,
    )
  }, [baseHandleChipDismissByLabel, showGroupedPage, routerAsPath, serverFilters, router])

  // Custom calculation for totalSelectedFilters that includes URL-based selections for grouped pages
  const effectiveTotalSelectedFilters = useEnhancedFilterCount(
    selectedFiltersState,
    showGroupedPage,
    routerAsPath,
    serverFilters,
  )

  return {
    selectedFiltersState,
    selectedFilterChips,
    clearAllFilters,
    handleCheckboxFilterChange,
    handleColorChange,
    handleChipDismissByLabel,
    effectiveTotalSelectedFilters,
  }
}
